import { useState } from "react";
import { Link } from "react-router-dom";
import { RunRecap } from "./RunRecap";
import { useNotificationPrefs } from "../hooks/useNotificationPrefs";
import {
  ACT_PENDING_LABEL,
  BAR_LOAD_ERROR_LINE,
  TRY_AGAIN_ACT,
  actStartsAPass,
  barAct,
  barLines,
  recapOf,
  type NextStep,
} from "../lib/nextStep";
import { ALLOWANCE_REFUSED_LINE, NO_WORK_PENDING_LINE } from "../lib/triggerCopy";

// NextStepBar (0051 slice 2): the product page's one next step - one state,
// one act - as the server serves it (lib/nextStep words it; nothing here
// decides a state, an act or a count). The page's first block: its lines,
// then its one act (full width on a phone), then, under the act, the one line
// that says why a Continue was refused. No count beside the act (FD-10), no
// dismiss, no auto-dismiss: the result stays until the server's exit.
export function NextStepBar({
  step,
  loading,
  loadError,
  onRetryLoad,
  productId,
  reviewHref,
  checkHref,
  onAct,
  actPending,
  refusedLine,
  errorLine,
  onReadSettled,
  settledOpens,
}: {
  step: NextStep | undefined;
  loading: boolean;
  loadError: boolean;
  onRetryLoad: () => void;
  productId: string;
  // Where the review act lands: the first blocking item (0167 N3).
  reviewHref: string;
  // Where the result's earlier-answer line lands (0164 R4).
  checkHref: string;
  // Continue / Try again: the page's trigger.
  onAct: () => void;
  actPending: boolean;
  // The refused-Continue line when the page already knows nothing is left.
  refusedLine: string | null;
  // A refused trigger's line (lib/triggerCopy words it).
  errorLine: string | null;
  // The result's settled line: the page opens the spec column at the lowest
  // layer the pass closed.
  onReadSettled: () => void;
  // Whether the page has such a layer to open (the settled line's own).
  settledOpens: boolean;
}) {
  const notificationsOnComplete = useNotificationPrefs((s) => s.notificationsOnComplete);
  const setNotificationsOnComplete = useNotificationPrefs((s) => s.setNotificationsOnComplete);
  // Bumped once a permission request answers, so a denied answer (which no
  // store change reports) hides the notify act.
  const [, setPermissionAsked] = useState(0);

  const sectionClass = "rounded-md border border-ink-800 bg-ink-900/40 px-5 py-4 space-y-3";

  // Loading: no line and no act, never a false state.
  if (loading) {
    return <section data-testid="next-step-bar" aria-label="Next step" className={sectionClass} />;
  }
  if (loadError) {
    return (
      <section data-testid="next-step-bar" aria-label="Next step" className={sectionClass}>
        <p role="alert" className="text-sm text-ink-200">
          {BAR_LOAD_ERROR_LINE}
        </p>
        <button
          type="button"
          onClick={onRetryLoad}
          className="font-mono text-[11px] uppercase tracking-widest text-ink-400 hover:text-ink-200"
        >
          {TRY_AGAIN_ACT}
        </button>
      </section>
    );
  }
  if (!step) {
    return <section data-testid="next-step-bar" aria-label="Next step" className={sectionClass} />;
  }

  const act = barAct(step);
  const actButtonClass =
    "w-full sm:w-auto rounded-md bg-accent px-4 py-2 text-center text-sm font-medium text-ink-950 hover:bg-accent-glow transition-colors disabled:cursor-not-allowed disabled:bg-accent/40";

  // The notify act shows only where a browser notification can work: the
  // browser has Notification, it has not denied it (a denied permission answers
  // every request "denied" at once), and the founder has not already turned
  // "notify on completion" on with permission granted. Until the email ships
  // the tab must stay open.
  const notifyShown =
    typeof Notification !== "undefined" &&
    Notification.permission !== "denied" &&
    !(notificationsOnComplete && Notification.permission === "granted");
  const askToNotify = async () => {
    const answer = await Notification.requestPermission();
    if (answer !== "denied") setNotificationsOnComplete(true);
    setPermissionAsked((n) => n + 1);
  };

  let actElement: React.ReactNode = null;
  if (act?.kind === "review") {
    actElement = (
      <Link to={reviewHref} data-testid="next-step-act" className={`inline-block ${actButtonClass}`}>
        {act.label}
      </Link>
    );
  } else if (actStartsAPass(act)) {
    actElement = (
      <button
        type="button"
        data-testid="next-step-act"
        onClick={onAct}
        disabled={actPending || refusedLine !== null}
        className={actButtonClass}
      >
        {actPending ? ACT_PENDING_LABEL : act.label}
      </button>
    );
  } else if (act?.kind === "notify" && notifyShown) {
    actElement = (
      <button
        type="button"
        data-testid="next-step-act"
        onClick={() => void askToNotify()}
        className="w-full sm:w-auto rounded-md border border-ink-700 px-4 py-2 text-center text-sm text-ink-200 hover:border-ink-600 hover:text-ink-100 transition-colors"
      >
        {act.label}
      </button>
    );
  }

  // Under a Continue or Try again: why it was refused, one line. The page
  // passes refusedLine when it already knows nothing is left, errorLine when
  // the server refused the trigger.
  const caption = actStartsAPass(act) ? (refusedLine ?? errorLine) : null;
  // A refused Continue on a settled spec is a calm state, not an error: in
  // ink, announced politely (0051 slice 3). Every other caption is an alert.
  const calm = caption === NO_WORK_PENDING_LINE;
  const alerted = caption !== null && !calm;

  return (
    <section data-testid="next-step-bar" aria-label="Next step" className={sectionClass}>
      {step.state === "result" ? (
        <RunRecap
          recap={recapOf(step)}
          productId={productId}
          checkHref={checkHref}
          onReadSettled={onReadSettled}
          settledOpens={settledOpens}
        />
      ) : (
        barLines(step).map((line) => (
          <p key={line} data-testid="next-step-line" className="text-sm text-ink-100">
            {line}
          </p>
        ))
      )}
      {actElement && <div>{actElement}</div>}
      {/* The calm caption's live region: mounted, empty and taking no room,
          under every Continue or Try again before any refusal, so only its
          text changes and a screen reader announces it (a region mounted
          with its words is not announced). */}
      {actStartsAPass(act) && !alerted && (
        <p role="status" className="text-xs text-ink-300 empty:sr-only">
          {calm ? caption : null}
        </p>
      )}
      {alerted && (
        <p role="alert" className="text-xs text-danger">
          {caption === ALLOWANCE_REFUSED_LINE ? <RefusedLine /> : caption}
        </p>
      )}
    </section>
  );
}

// RefusedLine renders the refused-Continue line with its "Settings" as a link
// to Settings; the words around it stay plain, so the line reads exactly
// ALLOWANCE_REFUSED_LINE (no new string).
function RefusedLine() {
  const word = "Settings";
  const at = ALLOWANCE_REFUSED_LINE.lastIndexOf(word);
  if (at < 0) return <>{ALLOWANCE_REFUSED_LINE}</>;
  return (
    <>
      {ALLOWANCE_REFUSED_LINE.slice(0, at)}
      <Link to="/settings" className="underline underline-offset-2 hover:text-ink-100">
        {word}
      </Link>
      {ALLOWANCE_REFUSED_LINE.slice(at + word.length)}
    </>
  );
}
